import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const DESKTOP_MEDIA_QUERY = '(min-width: 64rem)';

function isDesktopViewport() {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
    ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    : true;
}

interface NavigationPanelStateValue {
  isDesktop: boolean;
  isDesktopNavOpen: boolean;
  isMobileNavOpen: boolean;
  isNavOpen: boolean;
  toggleNavigation: () => void;
}

const defaultNavigationPanelState: NavigationPanelStateValue = {
  isDesktop: true,
  isDesktopNavOpen: true,
  isMobileNavOpen: false,
  isNavOpen: true,
  toggleNavigation: () => undefined,
};

const NavigationPanelStateContext = createContext<NavigationPanelStateValue>(
  defaultNavigationPanelState,
);

export function NavigationPanelStateProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
  const [isDesktopNavOpen, setIsDesktopNavOpen] = useState(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => {
        mediaQueryList.removeEventListener('change', handleChange);
      };
    }

    mediaQueryList.addListener(handleChange);
    return () => {
      mediaQueryList.removeListener(handleChange);
    };
  }, []);

  const value = useMemo<NavigationPanelStateValue>(
    () => ({
      isDesktop,
      isDesktopNavOpen,
      isMobileNavOpen,
      isNavOpen: isDesktop ? isDesktopNavOpen : isMobileNavOpen,
      toggleNavigation: () => {
        if (isDesktop) {
          setIsDesktopNavOpen((currentValue) => !currentValue);
        } else {
          setIsMobileNavOpen((currentValue) => !currentValue);
        }
      },
    }),
    [isDesktop, isDesktopNavOpen, isMobileNavOpen],
  );

  return (
    <NavigationPanelStateContext.Provider value={value}>
      {children}
    </NavigationPanelStateContext.Provider>
  );
}

export function useNavigationPanelState() {
  return useContext(NavigationPanelStateContext);
}
